import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import Page from '../../../components/Page';
import Breadcrumb from '../../../components/Breadcrumb';
import client from '../../../lib/feathers';
import SweepstakesModal from '../../../components/SweepstakesModal';

export default function SignupSummary() {
  const [data, setData] = useState();
  const [currentMonthModalOpen, setCurrentMonthModalOpen] = useState(false);
  const [lastMonthModalOpen, setLastMonthModalOpen] = useState(false);

  const loadData = async () => {
    const service = client.service('/admin/report-signup-summary');
    const result = await service.find();
    setData(result);
  };

  useEffect(() => {
    loadData();
  }, []);

  const StatCard = ({ label, signups, referrals }) => {
    const referralRate = signups > 0 ? Math.round((referrals / signups) * 100) : 0;
    return (
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
          <dd className="mt-1 text-3xl font-semibold text-gray-900">{signups || 0}</dd>
          <div className="mt-2 text-sm text-gray-600">
            <span className="font-medium text-green-600">{referrals || 0}</span> via referral ({referralRate}%)
          </div>
        </div>
      </div>
    );
  };

  const TopReferrersTable = ({ title, referrers, onDrawSweepstakes }) => {
    if (!referrers || referrers.length === 0) {
      return (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button
              onClick={onDrawSweepstakes}
              className="px-4 py-2 bg-primary text-white rounded hover:opacity-90 text-sm font-medium"
            >
              Draw Sweepstakes Winner
            </button>
          </div>
          <p className="text-gray-500">No referrals this period</p>
        </div>
      );
    }

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <button
            onClick={onDrawSweepstakes}
            className="px-4 py-2 bg-primary text-white rounded hover:opacity-90 text-sm font-medium"
          >
            Draw Sweepstakes Winner
          </button>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">User</th>
              <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Referrals</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {referrers.map((referrer, idx) => (
              <tr key={referrer.id || idx}>
                <td className="py-2">
                  <Link to={`/admin/users/${referrer.id}`} className="text-red-600 hover:text-red-900">
                    {referrer.firstName} {referrer.lastName}
                  </Link>
                  <span className="text-gray-500 text-sm ml-2">@{referrer.username}</span>
                </td>
                <td className="py-2 text-right font-medium">{referrer.referral_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const UsersTable = ({ title, users }) => {
    if (!users || users.length === 0) {
      return (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
          <p className="text-gray-500">No signups this period</p>
        </div>
      );
    }

    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Name</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Email</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Referred By</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">Signed Up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user, idx) => (
                <tr key={user.id || idx}>
                  <td className="py-2">
                    <Link to={`/admin/users/${user.id}`} className="text-red-600 hover:text-red-900">
                      {user.firstName} {user.lastName}
                    </Link>
                    <span className="text-gray-500 text-sm ml-2">@{user.username}</span>
                  </td>
                  <td className="py-2 text-sm text-gray-600">{user.email}</td>
                  <td className="py-2 text-sm">
                    {user.referrer_username ? (
                      <span className="text-green-600">
                        {user.referrer_first_name} {user.referrer_last_name}
                        <span className="text-gray-400 ml-1">@{user.referrer_username}</span>
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-500">
                    {formatDistance(new Date(user.createdAt), new Date(), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Page title="Signup Summary">
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb elements={[
            { name: 'Reports', href: `/admin/reports` },
            { name: 'Signup Summary' }
          ]} />
        </nav>
      </div>

      <div className="py-5">
        {!data && <p>Loading...</p>}
        {data && (
          <>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Signup Summary</h1>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
              <StatCard
                label={data.current_month_label?.trim() || 'This Month'}
                signups={parseInt(data.current_month_signups) || 0}
                referrals={parseInt(data.current_month_referrals) || 0}
              />
              <StatCard
                label={data.last_month_label?.trim() || 'Last Month'}
                signups={parseInt(data.last_month_signups) || 0}
                referrals={parseInt(data.last_month_referrals) || 0}
              />
            </div>

            {/* Top Referrers */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-8">
              <TopReferrersTable
                title={`Top Referrers - ${data.current_month_label?.trim() || 'This Month'}`}
                referrers={data.current_month_top_referrers}
                onDrawSweepstakes={() => setCurrentMonthModalOpen(true)}
              />
              <TopReferrersTable
                title={`Top Referrers - ${data.last_month_label?.trim() || 'Last Month'}`}
                referrers={data.last_month_top_referrers}
                onDrawSweepstakes={() => setLastMonthModalOpen(true)}
              />
            </div>

            {/* New Users Lists */}
            <div className="space-y-8">
              <UsersTable
                title={`New Users - ${data.current_month_label?.trim() || 'This Month'}`}
                users={data.current_month_users}
              />
              <UsersTable
                title={`New Users - ${data.last_month_label?.trim() || 'Last Month'}`}
                users={data.last_month_users}
              />
            </div>
          </>
        )}
      </div>

      {/* Sweepstakes Modals */}
      {data && (
        <>
          <SweepstakesModal
            isOpen={currentMonthModalOpen}
            onClose={() => setCurrentMonthModalOpen(false)}
            monthLabel={data.current_month_label?.trim() || 'This Month'}
            users={data.current_month_users}
            topReferrers={data.current_month_top_referrers}
          />
          <SweepstakesModal
            isOpen={lastMonthModalOpen}
            onClose={() => setLastMonthModalOpen(false)}
            monthLabel={data.last_month_label?.trim() || 'Last Month'}
            users={data.last_month_users}
            topReferrers={data.last_month_top_referrers}
          />
        </>
      )}
    </Page>
  );
}
